<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useAuthStore } from '@/store/auth'
import { messageOf } from '@/utils/messages'
// 본인 수신여부(문자·이메일·마케팅). 업무 안내 발송은 이 값이 Y일 때만 보낸다. 비밀번호 찾기 문자·이메일 인증 메일은 이 설정과 상관없다.
const ITEMS = [['smsReceiveAt', '문자 수신', '교육 일정·학습 안내 문자와 카카오 알림톡'], ['emailReceiveAt', '이메일 수신', '교육 일정·학습 안내 이메일'], ['marketingReceiveAt', '마케팅 정보 수신', '새 과정·행사 등 광고성 안내(문자·이메일 수신도 Y여야 받습니다)']]
const auth = useAuthStore(), saved = ref(null), draft = ref(null), busy = ref(false), error = ref(''), notice = ref('')
const dirty = computed(() => Boolean(saved.value) && ITEMS.some(([key]) => draft.value[key] !== saved.value[key]))
const ready = computed(() => Boolean(draft.value) && ITEMS.every(([key]) => ['Y', 'N'].includes(draft.value[key])))
let alive = true
onBeforeUnmount(() => { alive = false })
function accept(value) { saved.value = value; draft.value = Object.fromEntries(ITEMS.map(([key]) => [key, value[key]])) }
async function load() {
  busy.value = true; error.value = ''
  try { const value = await auth.client.request('/api/user/member/receive'); if (alive) accept(value) }
  catch (cause) { if (alive) error.value = messageOf(cause) } finally { if (alive) busy.value = false }
}
async function save() {
  if (busy.value || !ready.value || !dirty.value) return
  busy.value = true; error.value = ''; notice.value = ''
  try { const value = await auth.client.request('/api/user/member/receive', { method: 'PUT', body: { ...draft.value, reason: '본인 수신 설정 변경' } }); if (alive) { accept(value); notice.value = '수신 설정을 저장했습니다.' } }
  catch (cause) { if (alive) error.value = messageOf(cause) } finally { if (alive) busy.value = false }
}
const changedAt = computed(() => saved.value?.receiveChangedAt ? new Date(saved.value.receiveChangedAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }) : null)
onMounted(load)
</script>
<template>
  <section class="panel receive-panel" aria-labelledby="receive-heading">
    <div class="receive-heading"><div><h2 id="receive-heading">메시지 수신 설정</h2><p class="muted">교육 안내를 받을 방법을 고르세요. 정하지 않은 항목(미확인)으로는 안내를 보내지 않습니다.</p></div></div>
    <v-alert v-if="error" role="alert" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert><v-alert v-if="notice" role="status" type="success" variant="tonal" class="mb-4">{{ notice }}</v-alert>
    <form v-if="draft" @submit.prevent="save">
      <fieldset :disabled="busy" class="receive-list">
        <div v-for="[key, title, detail] in ITEMS" :key="key" class="receive-item">
          <div><h3>{{ title }}</h3><p>{{ detail }}</p><p v-if="!saved[key]" class="receive-unknown">미확인</p></div>
          <div class="receive-choice" role="radiogroup" :aria-label="`${title} 여부`">
            <label><input v-model="draft[key]" type="radio" value="Y" :name="key"> 받음</label>
            <label><input v-model="draft[key]" type="radio" value="N" :name="key"> 받지 않음</label>
          </div>
        </div>
      </fieldset>
      <p class="muted receive-note">비밀번호 찾기 인증번호 문자, 이메일 인증 메일, 학습 본인인증은 이 설정과 상관없이 보냅니다.<template v-if="changedAt"> 마지막 변경: {{ changedAt }}</template></p>
      <div class="receive-actions"><v-btn type="submit" :loading="busy" :disabled="busy || !ready || !dirty">수신 설정 저장</v-btn></div>
    </form>
  </section>
</template>
<style scoped>
.receive-panel{max-width:900px;margin-top:24px}.receive-heading h2{font-size:19px;margin-bottom:6px}
.receive-list{border:0;padding:0;margin:18px 0 0;display:grid;gap:0}
.receive-item{display:flex;justify-content:space-between;align-items:center;gap:18px;padding:16px 0;border-top:1px solid #edf1f4}
.receive-item h3{font-size:15px;margin:0 0 4px}.receive-item p{margin:0;font-size:13px;color:#64748b}.receive-item .receive-unknown{color:#b45309;font-weight:700;margin-top:4px}
.receive-choice{display:flex;gap:16px;flex-shrink:0;font-size:14px}.receive-choice label{display:inline-flex;align-items:center;gap:6px;cursor:pointer}
.receive-note{margin:14px 0}.receive-actions{display:flex;justify-content:flex-end}
@media(max-width:640px){.receive-item{flex-direction:column;align-items:flex-start}}
</style>
